import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ShoppingBag, Heart, Loader2, CheckCircle,
  AlertTriangle, Package, ArrowRight
} from 'lucide-react';
import axios from 'axios';
import { MerchTransparencyNotice } from './components/MerchTransparencyNotice';
import { useImageDisplayConfig } from '../hooks/useSiteContent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ShopPage = () => {
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const imgConfig = useImageDisplayConfig();

  const orderSuccess = searchParams.get('order') === 'success';

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/public/shop/items`);
      setItems(res.data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#BA1826]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="shop-page">
      {/* Header */}
      <div className="bg-[#0A0A0A] py-8 px-6">
        <div className="max-w-3xl mx-auto text-center relative">
          <Link to="/" className="inline-flex items-center gap-2 mb-4">
            <Heart className="w-5 h-5 text-[#BA1826]" fill="#BA1826" />
            <span className="font-heading font-bold text-white text-sm">Novena Foundation</span>
          </Link>
          <h1 className="font-heading text-3xl font-bold text-white mb-2" data-testid="shop-title">Shop</h1>
          <p className="text-gray-400 text-sm">Support our mission — every purchase helps families in need</p>
        </div>
      </div>

      <main className="max-w-3xl mx-auto px-6 py-8">
        <MerchTransparencyNotice variant="shop" />

        {/* Order Success */}
        {orderSuccess && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-8 mb-6 text-center max-w-lg mx-auto" data-testid="order-success">
            <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
            <h2 className="font-heading text-xl font-bold text-green-800 mb-2">Order Confirmed!</h2>
            <p className="text-sm text-green-700 mb-1">Thank you for your purchase. Your payment has been received.</p>
            <p className="text-xs text-green-600">A confirmation email with your order details and shipping information has been sent. We'll notify you when your order ships.</p>
            {searchParams.get('order_id') && (
              <p className="text-[10px] text-green-500 mt-3 font-mono">Order: {searchParams.get('order_id')}</p>
            )}
          </div>
        )}

        {/* Product Grid */}
        {items.length === 0 ? (
          <div className="text-center py-16">
            <Package className="w-10 h-10 text-[#9CA3AF] mx-auto mb-3" />
            <p className="text-sm text-[#4B5563]">No items available right now. Check back soon!</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-5 mt-6" data-testid="shop-items-grid">
            {items.map((item) => {
              const isSoldOut = item.is_sold_out;
              const isLow = item.is_low_stock;
              const hasVariations = item.variations?.length > 0;

              return (
                <Link to={`/shop/${item.id}`} key={item.id}
                  className={`bg-white border rounded-xl overflow-hidden transition-all hover:shadow-md block ${
                    isSoldOut ? 'opacity-60 border-[#E5E7EB]' : 'border-[#E5E7EB]'
                  }`}
                  data-testid={`shop-item-${item.id}`}
                >
                  {/* Product Image */}
                  <div className={`${item.image || !imgConfig.hide_empty_product_images ? 'h-48' : ''} bg-[#F1F5F9] relative`}>
                    {item.image ? (
                      <div className="w-full h-full flex">
                        <img src={item.image} alt={item.name} className={`${item.image_2 ? 'w-1/2' : 'w-full'} h-full object-cover`} />
                        {item.image_2 && <img src={item.image_2} alt={`${item.name} alt`} className="w-1/2 h-full object-cover border-l border-[#E5E7EB]" />}
                      </div>
                    ) : imgConfig.hide_empty_product_images ? null : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-12 h-12 text-[#D1D5DB]" />
                      </div>
                    )}
                    {isSoldOut && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <span className="bg-red-600 text-white text-sm font-bold px-4 py-2 rounded">SOLD OUT</span>
                      </div>
                    )}
                    {isLow && !isSoldOut && (
                      <div className="absolute top-2 right-2 bg-amber-500 text-white text-[10px] font-bold px-2 py-1 rounded flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> Almost Sold Out
                      </div>
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="p-5">
                    <h3 className="font-heading text-lg font-bold text-[#0A0A0A] mb-1" data-testid={`item-name-${item.id}`}>
                      {item.name}
                    </h3>
                    {item.description && <p className="text-xs text-[#4B5563] mb-3 leading-relaxed line-clamp-2">{item.description}</p>}

                    <div className="flex items-center justify-between mb-3">
                      <span className="font-heading text-xl font-bold text-[#BA1826]" data-testid={`item-price-${item.id}`}>
                        ${item.price.toFixed(2)}
                      </span>
                      {hasVariations && (
                        <span className="text-[10px] text-[#4B5563] bg-[#F1F5F9] px-2 py-0.5 rounded">
                          {item.variations.map(v => v.name).join(' / ')}
                        </span>
                      )}
                    </div>

                    {item.impact_blurb && (
                      <p className="text-xs text-[#4B5563] italic leading-snug border-l-2 border-[#BA1826]/30 pl-2 mb-3"
                        data-testid={`item-impact-${item.id}`}>
                        {item.impact_blurb}
                      </p>
                    )}

                    <div className={`w-full py-2.5 text-sm font-semibold rounded-lg text-center flex items-center justify-center gap-2 ${
                      isSoldOut
                        ? 'bg-gray-100 text-gray-400'
                        : 'bg-[#BA1826] text-white'
                    }`}>
                      {isSoldOut ? 'Sold Out' : <><ShoppingBag className="w-4 h-4" /> View & Buy</>}
                    </div>

                    <p className="text-[9px] text-[#94A3B8] text-center mt-2 leading-relaxed">
                      Merchandise purchases support the mission of Novena Humanitarian Foundation and are not tax-deductible donations.
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-12 pt-6 pb-8 border-t border-[#E5E7EB]">
          <p className="text-[11px] text-[#9CA3AF]">
            All proceeds support <Link to="/" className="font-semibold text-[#BA1826] hover:underline">Novena Humanitarian Foundation</Link>
          </p>
          <p className="text-[10px] text-[#D1D5DB] mt-1 italic">Transparency without exploitation. Support without shame.</p>
        </div>
      </main>
    </div>
  );
};

export default ShopPage;
